/**
 * cba-gift-claim.js — the free-gift popup reached from an email link
 * (?claim=flower) (plan 6.1e: was the snippet's inline script). Markup:
 * snippets/cba-gift-claim, a <cba-dialog name="gift-claim">.
 *
 * - Pick a finish (the gift product's variants), then "Add to cart — free" applies the
 *   gift discount and adds the hidden gift variant with a _gift property,
 *   through @theme/cart (an ES module; DESIGN-PLAN 7.G2). A refused or failed
 *   add says why under the button (site audit B14.1).
 * - If the gift is already in the cart, it opens on the confirmation step.
 * - A visitor who came to claim does not also get the scratch popup this
 *   session.
 * - Opening and the add are tracked through @theme/klaviyo (consent-gated
 *   inside the helper).
 */
import { addToCart, applyDiscountCode, getCart, CartDeclinedError } from '@theme/cart';
import { track } from '@theme/klaviyo';

(function () {
  var root = document.querySelector('[data-gift-claim]');
  if (!root || root.__cbaGiftInit) return;
  root.__cbaGiftInit = true;
  /* Opening, closing, the backdrop, Escape and the scroll lock are the
     shared <cba-dialog>'s (plan 3.5). */
  var host       = root.closest('cba-dialog');
  var swatches   = root.querySelector('[data-gift-swatches]');
  var addBtn     = root.querySelector('[data-gift-add]');
  var productImg = root.querySelector('[data-gift-product-img]');
  var doneImg    = root.querySelector('[data-gift-done-img]');
  var steps      = root.querySelectorAll('.cba-gift-claim__step');
  var addLabel   = addBtn ? addBtn.textContent : '';

  // The discount code and the gift product's variants come from Theme
  // settings > CBA integrations, on the dialog's data attributes
  // (snippets/cba-gift-claim). The first variant is the one picked at first.
  var GIFT_DISCOUNT = root.dataset.discount || '';
  var GIFT_VARIANTS = String(root.dataset.variants || '').split(',').map(Number).filter(Boolean);
  var selectedVariant = GIFT_VARIANTS[0];

  function showStep(name) {
    steps.forEach(function (s) { s.hidden = (s.getAttribute('data-gift-step') !== name); });
  }

  /* ---- open / close --------------------------------------------------- */
  var opened = false;
  function open(step) {
    showStep(step || 'pick');
    customElements.whenDefined('cba-dialog').then(function () { host.open(); });
    if (!opened) {
      opened = true;
      // Optional analytics (consent-gated inside the helper; add-to-cart itself
      // is functional and never gated).
      track('Viewed Gift Claim', { step: step || 'pick' });
    }
  }
  function close() {
    host.close();
  }
  // "Go to cart" on the done step: the cart-drawer already intercepts /cart links to
  // open the drawer — also close this popup so it doesn't linger open behind the drawer.
  var gotoCart = root.querySelector('[data-gift-gotocart]');
  if (gotoCart) gotoCart.addEventListener('click', function () { close(); });

  /* ---- finish picker -------------------------------------------------- */
  if (swatches) {
    swatches.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.value) return;
      selectedVariant = parseInt(t.value, 10);
      var img = t.getAttribute('data-gift-img');
      if (img && productImg) productImg.src = img;
    });
  }

  /* ---- add gift (free) ---- The code first, added to the codes already on
     the cart, then the gift line with its _gift property. */
  var giftError = root.querySelector('[data-gift-error]');
  function showGiftError(text) {
    if (!giftError) return;
    giftError.textContent = text || '';
    giftError.hidden = !text;
  }
  function addGift(variantId) {
    var options = { element: addBtn, context: 'dialog' };
    return applyDiscountCode(GIFT_DISCOUNT, options).then(function () {
      return addToCart([{ id: variantId, quantity: 1, properties: { _gift: GIFT_DISCOUNT } }], options);
    });
  }

  addBtn.addEventListener('click', function () {
    addBtn.disabled = true;
    addBtn.textContent = root.dataset.addingLabel || addLabel;
    showGiftError('');
    addGift(selectedVariant).then(function () {
      track('Claimed Gift From Email', { variant: selectedVariant });
      // Behave like the sign-up (scratch) popup: just close. The added-to-cart
      // popup (cba-cart-feedback, on Shopify's shopify:cart:lines-update) flies
      // the gift into the cart icon: that is the confirmation. The done step
      // is for the already-in-cart re-open, so the gift is not added twice.
      close();
    }).catch(function (error) {
      showGiftError(error instanceof CartDeclinedError && error.message ? error.message : root.dataset.error);
    }).then(function () {
      addBtn.disabled = false;
      addBtn.textContent = addLabel;
    });
  });

  /* ---- trigger: ?claim=flower ----------------------------------------- */
  var wantsClaim = false;
  try { wantsClaim = new URLSearchParams(location.search).get('claim') === 'flower'; } catch (e) {}
  if (!wantsClaim) return;

  // This visitor came to CLAIM (already a subscriber) — don't also nag them with
  // the sign-up scratch popup this session. Set its per-session guard so it stays
  // shut. (Light, one-key coordination; keeps this popup otherwise decoupled.)
  try { sessionStorage.setItem('cba_scratch_shown_session', '1'); } catch (e) {}

  // If the gift is already in the cart, open straight to the confirmation.
  getCart()
    .then(function (cart) {
      var has = (cart.items || []).some(function (it) {
        return GIFT_VARIANTS.indexOf(it.variant_id) !== -1 || GIFT_VARIANTS.indexOf(it.id) !== -1;
      });
      if (has && doneImg && productImg) doneImg.src = productImg.src;
      open(has ? 'done' : 'pick');
    })
    .catch(function () { open('pick'); });
})();
